import { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  Button,
  DateRangePicker,
  FontSizeIcon,
  IDropdownItem,
  Input,
  MultiSelect,
  NumberInput,
  Pagination,
  Tabs,
  Tooltip,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useForm, Controller } from 'react-hook-form';

import { AuthContext } from 'auth';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { ELogType } from 'types';
import { formatDateForServer } from 'utils';

import s from './EventsFilter.module.scss';
import { IFilterProps, IFormValues } from './interfaces';
import { getOptions } from './options';

export const EventsFilter: FC<IFilterProps> = ({
  isLogs,
  setFilters,
  onPageChange,
  currentPage,
  lastPage = 0,
  setIsOperatorLogs,
  isOperatorLogs,
  isActiveOperator,
  isActiveRegister,
  setIsActiveRegister,
}) => {
  const { t, lang } = useContext(LocalizationContext);
  const {
    data: { me },
  } = useContext(AuthContext);

  const options = useMemo<Array<IDropdownItem<string>>>(
    () =>
      getOptions(t).map(({ title, value, sub }) => ({
        title,
        value: typeof value !== 'string' ? value.join(',') : value,
        sub,
      })),
    [t]
  );

  const { control, handleSubmit } = useForm<IFormValues>({
    defaultValues: {
      search: '',
    },
  });

  const today = dayjs();
  const [searchStr, setSearchStr] = useState('');
  const [eventTypes, setEventTypes] = useState<Array<string> | null>(null);
  const [startDate, setStartDate] = useState<string | null>(
    today.add(-1, 'months').hour(0).minute(0).second(0).format()
  );
  const [endDate, setEndDate] = useState<string | null>(
    today.hour(23).minute(59).second(59).format()
  );

  const onPageInputChange = (value: number | ''): void => {
    const pageNumber = typeof value === 'number' ? value : Number(value);
    if (!isNaN(pageNumber)) {
      if (onPageChange) {
        onPageChange(pageNumber);
      }
    }
  };

  const onSubmitPage = (data: { page: number | '' }): void => {
    if (data.page !== undefined && data.page !== '') {
      onPageInputChange(data.page);
    }
  };

  const onSubmitSearch = (data: { search: string | '' }): void => {
    if (data.search === undefined || data.search === '') return;
    setSearchStr(data.search);
  };

  useEffect(() => {
    const types =
      eventTypes?.flatMap((type) => type.split(',') as Array<ELogType>) ?? [];

    const isSearchValid =
      searchStr === '' || (searchStr.length >= 2 && searchStr.length <= 50);

    setFilters({
      q: isSearchValid ? searchStr : '',
      types,
      from: formatDateForServer(dayjs(startDate)),
      to: formatDateForServer(dayjs(endDate)),
    });
  }, [setFilters, searchStr, eventTypes, startDate, endDate]);

  const size = useWindowResize();

  return (
    <div className={s.root}>
      <form onSubmit={handleSubmit(onSubmitSearch)} className={s.search}>
        <Controller
          name="search"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Input
              value={value}
              onChange={(val) => {
                onChange(val);
                if (val === '') {
                  setSearchStr(val);
                }
              }}
              variant="secondary"
              rightContent={
                <Tooltip
                  content="Искать с учетом регистра"
                  contentClassName={s.tooltip}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setIsActiveRegister?.((prev) => !prev);
                    }}
                    className={cn(
                      s.button_register,
                      isActiveRegister && s.active
                    )}
                  >
                    <FontSizeIcon color={'secondary'} />
                  </button>
                </Tooltip>
              }
              error={searchStr.length >= 50 ? t('errors.maxLength', 50) : ''}
              placeholder={t('logs.search.title')}
              className={s.input_search}
            />
          )}
        />
        <Tooltip
          content={t('logs.search_info')}
          contentClassName={s.search__tooltip}
          position={size.width > 960 ? 'bottom' : 'bottom-right'}
        >
          <Button
            type="submit"
            className={s.button_submit}
            onlyIcon
            iconName="SearchIcon"
          />
        </Tooltip>
      </form>
      {isLogs && (
        <div className={s.date_block}>
          <DateRangePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            lastValidDate={today.format()}
            firstValidDate={dayjs('2022-03-01T00:00:00').format()}
            hasPreselectedRange={false}
            lang={lang}
          />
          {isActiveOperator &&
            isOperatorLogs !== undefined &&
            setIsOperatorLogs && (
              <Tabs
                options={[
                  {
                    iconName: 'OnlineWorkerIcon',
                    value: true,
                    tooltip: {
                      content: t('logs.switcher.operator'),
                      contentClassName: s.tooltip_content,
                      position: 'bottom',
                    },
                  },
                  {
                    iconName: 'LogIcon',
                    value: false,
                    tooltip: {
                      style: { width: '100px' },
                      content: t('logs.switcher.manager'),
                      contentClassName: s.tooltip_content,
                      position: 'bottom-right',
                    },
                  },
                ]}
                onChange={setIsOperatorLogs}
                value={isOperatorLogs}
                className={s.tabs_mobile}
              />
            )}
        </div>
      )}
      <div className={s.select_block}>
        <MultiSelect
          value={eventTypes}
          onChange={setEventTypes}
          variant="secondary"
          options={options}
          title={t('logs.categories.all')}
          className={s.input_select}
          dropdownsClassNames={s.input_select__dropdown}
          isSearch
          searchPlaceHolder={t('search.category')}
        />
      </div>
      {onPageChange && lastPage > 1 && currentPage && (
        <div className={s.pagination__block}>
          <Pagination
            onChange={onPageChange}
            lastPage={lastPage}
            currentPage={currentPage}
            className={s.pagination}
            isChose={true}
          />
          <form onSubmit={handleSubmit(onSubmitPage)}>
            <Controller
              name="page"
              control={control}
              render={({ field: { onChange } }) => (
                <NumberInput
                  value={currentPage === 1 ? '' : currentPage}
                  onChange={(val) => {
                    if (typeof val === 'number') {
                      if (val < 1) {
                        onChange(1);
                      } else if (val > lastPage) {
                        onChange(lastPage);
                      } else {
                        onChange(val);
                      }
                    } else {
                      onChange('');
                    }
                  }}
                  variant="secondary"
                  placeholder="№"
                  className={s.pagination_input}
                />
              )}
            />
            <button type="submit" style={{ display: 'none' }} />
          </form>{' '}
        </div>
      )}
      {isActiveOperator &&
        isOperatorLogs !== undefined &&
        setIsOperatorLogs &&
        me?.roles[0].alias !== 'operator' && (
          <Tabs
            options={[
              {
                iconName: 'OnlineWorkerIcon',
                value: true,
                tooltip: {
                  content: t('logs.switcher.operator'),
                  contentClassName: s.tooltip_content,
                  position: 'bottom',
                },
              },
              {
                iconName: 'LogIcon',
                value: false,
                tooltip: {
                  style: { width: '100px' },
                  content: t('logs.switcher.manager'),
                  contentClassName: s.tooltip_content,
                  position: 'bottom-right',
                },
              },
            ]}
            onChange={setIsOperatorLogs}
            value={isOperatorLogs}
            className={s.tabs}
          />
        )}
    </div>
  );
};
